import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Minus, Plus } from 'lucide-react';

export interface NumericStepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  label?: string;
  valueClassName?: string;
  inputWidthClassName?: string;
  className?: string;
  compact?: boolean;
  ariaLabel?: string;
}

export function NumericStepper({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  decimals = 0,
  label,
  valueClassName = 'text-white',
  inputWidthClassName = 'w-9',
  className = '',
  compact = false,
  ariaLabel,
}: NumericStepperProps) {
  const factor = Math.pow(10, decimals);

  const clampAndRound = useCallback(
    (raw: number) => {
      const clamped = Math.min(max, Math.max(min, raw));
      return Math.round(clamped * factor) / factor;
    },
    [min, max, factor]
  );

  const formatVal = useCallback(
    (v: number) => {
      const safe = Number.isFinite(v) ? clampAndRound(v) : min;
      return decimals > 0 ? safe.toFixed(decimals) : String(Math.round(safe));
    },
    [clampAndRound, decimals, min]
  );

  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => formatVal(value));

  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const didRepeatRef = useRef(false);

  useEffect(() => {
    if (!focused) {
      setDraft(formatVal(value));
    }
  }, [value, focused, formatVal]);

  const stopHold = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  }, []);

  useEffect(() => stopHold, [stopHold]);

  const applyDelta = useCallback(
    (delta: number) => {
      const next = clampAndRound(latestValueRef.current + delta);
      latestValueRef.current = next;
      onChangeRef.current(next);
    },
    [clampAndRound]
  );

  const startHold = (direction: -1 | 1) => {
    stopHold();
    didRepeatRef.current = false;
    let ticks = 0;

    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        ticks += 1;
        didRepeatRef.current = true;
        const multiplier = ticks > 28 ? 20 : ticks > 12 ? 5 : 1;
        applyDelta(direction * step * multiplier);
      }, 70);
    }, 360);
  };

  const handleStepClick = (direction: -1 | 1, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (didRepeatRef.current) {
      didRepeatRef.current = false;
      return;
    }
    const mult = e.shiftKey ? 10 : 1;
    applyDelta(direction * step * mult);
  };

  const commitDraft = () => {
    const parsed = decimals > 0 ? parseFloat(draft) : parseInt(draft, 10);
    if (!Number.isNaN(parsed)) {
      const next = clampAndRound(parsed);
      onChange(next);
      setDraft(formatVal(next));
    } else {
      setDraft(formatVal(value));
    }
  };

  const btnSizeCls = compact ? 'w-5 h-5' : 'w-5 h-5 sm:w-5 sm:h-5';

  return (
    <div
      className={`inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg ${
        label ? 'pl-2 pr-1 py-0.5' : 'p-0.5'
      } ${className}`}
    >
      {label && (
        <span className="text-white/50 text-[10px] font-medium select-none whitespace-nowrap">
          {label}
        </span>
      )}

      <div className="inline-flex items-center bg-black/35 border border-white/10 rounded-md p-0.5 gap-0.5">
        <button
          type="button"
          aria-label={`Decrease ${ariaLabel || label || 'value'}`}
          disabled={value <= min}
          onPointerDown={() => startHold(-1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          onClick={(e) => handleStepClick(-1, e)}
          className={`${btnSizeCls} rounded bg-white/[0.07] hover:bg-purple-500/30 active:bg-purple-500/50 disabled:opacity-30 disabled:pointer-events-none text-white/80 hover:text-white flex items-center justify-center select-none touch-manipulation transition-colors`}
        >
          <Minus className="size-3 stroke-[2.5]" />
        </button>

        <input
          type="text"
          inputMode={decimals > 0 ? 'decimal' : 'numeric'}
          aria-label={ariaLabel || label || 'Numeric value'}
          value={focused ? draft : formatVal(value)}
          onFocus={(e) => {
            setFocused(true);
            setDraft(formatVal(value));
            e.currentTarget.select();
          }}
          onChange={(e) => {
            const raw = e.target.value;
            setDraft(raw);
            const parsed = decimals > 0 ? parseFloat(raw) : parseInt(raw, 10);
            if (!Number.isNaN(parsed) && parsed >= min && parsed <= max) {
              onChange(Math.round(parsed * factor) / factor);
            }
          }}
          onBlur={() => {
            setFocused(false);
            commitDraft();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              applyDelta(step * (e.shiftKey ? 10 : 1));
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              applyDelta(-step * (e.shiftKey ? 10 : 1));
            }
          }}
          className={`${inputWidthClassName} bg-transparent ${valueClassName} text-xs font-mono font-semibold text-center focus:outline-none focus:bg-white/10 rounded px-0.5 py-0.5`}
        />

        <button
          type="button"
          aria-label={`Increase ${ariaLabel || label || 'value'}`}
          disabled={value >= max}
          onPointerDown={() => startHold(1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          onClick={(e) => handleStepClick(1, e)}
          className={`${btnSizeCls} rounded bg-white/[0.07] hover:bg-purple-500/30 active:bg-purple-500/50 disabled:opacity-30 disabled:pointer-events-none text-white/80 hover:text-white flex items-center justify-center select-none touch-manipulation transition-colors`}
        >
          <Plus className="size-3 stroke-[2.5]" />
        </button>
      </div>
    </div>
  );
}

export default NumericStepper;
